import styled from '@emotion/styled';
import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceAround,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceAround,
  Crosshair,
} from 'lucide-react';
import React from 'react';
import UDIconButton from 'ud-ui/components/icon-button';
import InspectorSection from '../shared/inspector-section';

interface AlignmentSectionProps {
  editor: FabricEditor;
  active: fabric.Object[];
}

// общие пропсы всех кнопок секции — чтобы вариант и размер не разъезжались между ними
const buttonProps = { variant: 'default', size: 'sm' } as const;

const AlignmentSection: React.FC<AlignmentSectionProps> = ({ editor, active }) => {
  const a = editor.alignment;
  if (!a || active.length === 0) return null;

  const single = active.length === 1;
  const canDistribute = active.length >= 3;

  return (
    <InspectorSection title="Выравнивание">
      <Grid>
        {!single && (
          <>
            <UDIconButton {...buttonProps} tooltip="Выровнять влево" icon={<AlignStartVertical />} onClick={() => a.alignLeft()} />
            <UDIconButton
              {...buttonProps}
              tooltip="Центрировать по вертикали"
              icon={<AlignCenterVertical />}
              onClick={() => a.centerOnCanvasX()}
            />
            <UDIconButton {...buttonProps} tooltip="Выровнять вправо" icon={<AlignEndVertical />} onClick={() => a.alignRight()} />
            <UDIconButton {...buttonProps} tooltip="Выровнять сверху" icon={<AlignStartHorizontal />} onClick={() => a.alignTop()} />
            <UDIconButton
              {...buttonProps}
              tooltip="Центрировать по горизонтали"
              icon={<AlignCenterHorizontal />}
              onClick={() => a.centerOnCanvasY()}
            />
            <UDIconButton {...buttonProps} tooltip="Выровнять снизу" icon={<AlignEndHorizontal />} onClick={() => a.alignBottom()} />
          </>
        )}
        {single && (
          <>
            <UDIconButton
              {...buttonProps}
              tooltip="Центрировать по вертикали"
              icon={<AlignCenterVertical />}
              onClick={() => a.centerOnCanvasX(active[0])}
            />
            <UDIconButton
              {...buttonProps}
              tooltip="Центрировать по горизонтали"
              icon={<AlignCenterHorizontal />}
              onClick={() => a.centerOnCanvasY(active[0])}
            />
          </>
        )}
      </Grid>
      {!single && (
        <Grid>
          <UDIconButton
            {...buttonProps}
            tooltip="Центрировать первый к остальным (X)"
            icon={<Crosshair color="#007AFF" />}
            onClick={() => a.centerFirstToOthersX()}
          />
          <UDIconButton
            {...buttonProps}
            tooltip="Центрировать первый к остальным (Y)"
            icon={<Crosshair color="#FF9500" />}
            onClick={() => a.centerFirstToOthersY()}
          />
          <UDIconButton
            {...buttonProps}
            tooltip="Распределить по горизонтали"
            icon={<AlignHorizontalSpaceAround />}
            disabled={!canDistribute}
            onClick={() => a.distributeHorizontally()}
          />
          <UDIconButton
            {...buttonProps}
            tooltip="Распределить по вертикали"
            icon={<AlignVerticalSpaceAround />}
            disabled={!canDistribute}
            onClick={() => a.distributeVertically()}
          />
        </Grid>
      )}
    </InspectorSection>
  );
};

export default AlignmentSection;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  // кнопки фиксированной ширины (28px), поэтому центрируем их в ячейках
  justify-items: center;
`;
